import {inject, Injectable} from '@angular/core';
import {EMPTY, map, Observable, ReplaySubject, share} from 'rxjs';
import {ObjectData, ObjektDataService} from './objekt-data.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SearchFilterOptions, SearchParameters} from './objekt-search.service';
import {HelperService} from './helper.service';
import {UserAuthenticationService} from './user-authentication.service';
import {SwissSettingsService} from './swiss-settings.service';
import {OfflineObjektSearchService} from './offline-objekt-search.service';

@Injectable({
    providedIn: 'root',
})
export class OnlineObjektSearchService {
    private readonly objektDataService = inject(ObjektDataService);
    private readonly helperService = inject(HelperService);
    private readonly httpClient = inject(HttpClient);
    private readonly userAuthService = inject(UserAuthenticationService);

    private readonly objUrl = this.helperService.getSwissWebServiceConfig().objUrl;
    private readonly url = `${this.objUrl}/search`;
    private readonly urlDynamic = `${this.objUrl}/searchDynamic`;

    constructor(private swissSettings: SwissSettingsService, private offlineSearchService: OfflineObjektSearchService) {
    }

    public getFilterValues(): SearchFilterOptions {
        const filterValues = this.objektDataService.getObjektSucheDaten().pipe(share({connector: () => new ReplaySubject(1)}));

        return {
            placesFilterOptions: filterValues.pipe(map((data) => data.objektOrte)),
            projectLeaderFilterOptions: filterValues.pipe(map((data) => data.projektleiter)),
            statusFilterOptions: filterValues.pipe(map((data) => data.objektStatus)),
        };
    }

    public search({searchTerm, note4, place: places, projectLeader: projectLeaders, objStatus, useDynamicObjectSource}: SearchParameters): Observable<ObjectData[]> {
        switch (this.swissSettings?.getObjViewConfig() || 1) {
            case 1:
                return this.treeViewSearch({searchTerm, note4, place: places, projectLeader: projectLeaders, objStatus, useDynamicObjectSource});
            case 5:
            case 6:
            default:
                // legacy on client search
                return this.offlineSearchService.search({searchTerm, note4, place: places, projectLeader: projectLeaders, objStatus});
        }
    }

    private treeViewSearch({searchTerm, note4, place: places, projectLeader: projectLeaders, objStatus, useDynamicObjectSource = false}: SearchParameters) {
        if (!(searchTerm || note4 || places?.length || projectLeaders?.length || objStatus?.length)) {
            return EMPTY; // Return empty observable if no search parameters are provided
        }

        let params = new HttpParams();

        if (searchTerm) {
            params = params.set('searchTerm', searchTerm);
        }
        if (note4) {
            params = params.set('note4', note4);
        }
        for (const p of places ?? []) {
            params = params.append('place', p.town);
        }
        for (const l of projectLeaders ?? []) {
            params = params.append('projectLeader', l.id);
        }
        for (const s of objStatus ?? []) {
            params = params.append('objStatus', s.id);
        }

        const httpOptions = {...this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo), params};

        return this.httpClient.get<ObjectData[]>(useDynamicObjectSource ? this.urlDynamic : this.url, httpOptions);
    }
}
